import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';
import { AppState } from 'store/app-state';
import * as FeatureFlagSelectors from 'store/feature-flags/feature-flags.selectors';
import { FeatureFlag } from 'store/feature-flags/feature-flags.state';

/** IANA time zone used to interpret and present the maintenance restore time. */
const CENTRAL_TIME_ZONE = 'America/Chicago';

@Component({
  selector: 'maintenance',
  templateUrl: './maintenance.component.html',
  styleUrls: ['./maintenance.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [AsyncPipe]
})
export class MaintenanceComponent {
  private readonly store = inject(Store<AppState>);

  /**
   * The date/time the site is expected to be fully restored, formatted for
   * display (e.g. `Wednesday, September 9, at 6:00 AM CDT`). This is the day
   * after the scheduled maintenance window `End`, at 6:00 AM Central time.
   * Emits `null` when no maintenance window end is available.
   */
  readonly restoreDateTime$: Observable<string | null> = this.store
    .select(FeatureFlagSelectors.featureFlags)
    .pipe(
      map((flags) => {
        const end = this.getMaintenanceEnd(flags?.['DownForMaintenance']);
        return end ? this.formatRestoreDateTime(end) : null;
      })
    );

  /** Extracts the scheduled maintenance window end from the `DownForMaintenance` flag, if present and valid. */
  private getMaintenanceEnd(flag?: FeatureFlag): Date | null {
    const end = flag?.maintenanceWindowEndUtc;
    if (!end) {
      return null;
    }
    const parsed = new Date(end);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  /** Formats the restore date/time as the day after `end`, at 6:00 AM Central time. */
  private formatRestoreDateTime(end: Date): string {
    // Resolve the maintenance end to its Central-time calendar date.
    const centralParts = new Intl.DateTimeFormat('en-US', {
      timeZone: CENTRAL_TIME_ZONE,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    }).formatToParts(end);
    const year = Number(centralParts.find((part) => part.type === 'year')?.value);
    const month = Number(centralParts.find((part) => part.type === 'month')?.value);
    const day = Number(centralParts.find((part) => part.type === 'day')?.value);

    // The day after the end date, anchored at noon UTC to avoid DST/rollover edge cases.
    const restoreDay = new Date(Date.UTC(year, month - 1, day + 1, 12));

    const dateLabel = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    }).format(restoreDay);

    const timeZoneName =
      new Intl.DateTimeFormat('en-US', {
        timeZone: CENTRAL_TIME_ZONE,
        timeZoneName: 'short'
      })
        .formatToParts(restoreDay)
        .find((part) => part.type === 'timeZoneName')?.value ?? 'CDT';

    return `${dateLabel}, at 6:00 AM ${timeZoneName}`;
  }
}
